import { Icon } from '@sr/ui-library';
import {
  confirmUserAttribute,
  fetchUserAttributes,
  sendUserAttributeVerificationCode,
  setUpTOTP,
  updateMFAPreference,
  updateUserAttributes,
  verifyTOTPSetup,
} from 'aws-amplify/auth';
import QRCode from 'qrcode';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from '../../../Loader';
import CodeInput from './CodeInput';
import MethodChooser, { MfaMethod } from './MethodChooser';
import PhoneInputField, { isValidPhoneNumber } from './PhoneInputField';
import { ErrorBanner, SetupContainer } from './SetupContainer';

type SetupStep = 'choose' | 'phone' | 'qr' | 'sms_verify' | 'success';

interface MfaSetupScreenProps {
  onCancel?: () => void;
  onComplete: () => void;
  userEmail?: string;
}

const MfaSetupScreen = ({
  onCancel,
  onComplete,
  userEmail,
}: MfaSetupScreenProps) => {
  const [step, setStep] = useState<SetupStep>('qr');
  const [chosenMethod, setChosenMethod] = useState<MfaMethod | null>(null);

  const [setupUri, setSetupUri] = useState('');
  const [qrDataUrl, setQrDataUrl] = useState('');
  const [secretKey, setSecretKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);

  const [phoneNumber, setPhoneNumber] = useState<string | undefined>();
  const [existingPhone, setExistingPhone] = useState<null | string>(null);

  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resendMessage, setResendMessage] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  const generateQrCode = useCallback(async (uri: string) => {
    try {
      const dataUrl = await QRCode.toDataURL(uri, { margin: 2, width: 200 });

      setQrDataUrl(dataUrl);
    } catch {
      // Fallback: user can enter key manually
    }
  }, []);

  useEffect(() => {
    if (setupUri) generateQrCode(setupUri);
  }, [setupUri, generateQrCode]);

  const resetState = () => {
    setCode('');
    setError('');
    setSetupUri('');
    setQrDataUrl('');
    setSecretKey('');
    setShowSecret(false);
    setPhoneNumber(undefined);
    setExistingPhone(null);
    setChosenMethod(null);
  };

  const startTotpSetup = async () => {
    setIsLoading(true);
    setError('');
    try {
      const output = await setUpTOTP();
      const uri = output.getSetupUri('Screenrights', userEmail);

      setSetupUri(uri.toString());
      setSecretKey(output.sharedSecret);
      setStep('qr');
    } catch (err: any) {
      setError(err?.message || 'Failed to start MFA setup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpVerify = async () => {
    setIsLoading(true);
    setError('');
    try {
      await verifyTOTPSetup({ code: code.trim() });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setStep('success');
    } catch (err: any) {
      const message = err?.message || '';

      if (
        message.includes('Code mismatch') ||
        message.includes('EnableSoftwareTokenMFAException')
      ) {
        setError(
          'Invalid code. Please check your authenticator app and try again.',
        );
      } else {
        setError(message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const startSmsSetup = async () => {
    setIsLoading(true);
    setError('');
    try {
      const attrs = await fetchUserAttributes();
      const phone = attrs.phone_number;
      const verified = attrs.phone_number_verified === 'true';

      if (phone) {
        setExistingPhone(phone);
        setPhoneNumber(phone);
      }

      setStep('phone');
    } catch (err: any) {
      setError(
        err?.message || 'Failed to load phone number. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneSubmit = async () => {
    if (!phoneNumber || !isValidPhoneNumber(phoneNumber)) {
      setError('Please enter a valid phone number.');

      return;
    }

    setIsLoading(true);
    setError('');
    try {
      if (phoneNumber !== existingPhone) {
        await updateUserAttributes({
          userAttributes: { phone_number: phoneNumber },
        });
      }

      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });

      setStep('sms_verify');
    } catch (err: any) {
      setError(err?.message || 'Failed to set phone number. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSmsVerify = async () => {
    setIsLoading(true);
    setError('');
    try {
      await confirmUserAttribute({
        confirmationCode: code.trim(),
        userAttributeKey: 'phone_number',
      });
      await updateMFAPreference({ sms: 'PREFERRED' });
      setStep('success');
    } catch (err: any) {
      const message = err?.message || '';

      if (
        message.includes('CodeMismatchException') ||
        message.includes('mismatch')
      ) {
        setError('Invalid code. Please check your SMS and try again.');
      } else {
        setError(message || 'Verification failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const resendSmsCode = async () => {
    setError('');
    try {
      await sendUserAttributeVerificationCode({
        userAttributeKey: 'phone_number',
      });
      setResendCountdown(30);
      setResendMessage('Code sent!');
      setTimeout(() => setResendMessage(''), 3000);
    } catch (err: any) {
      setError(err?.message || 'Failed to resend code.');
    }
  };

  const totpInitRef = useRef(false);

  useEffect(() => {
    if (totpInitRef.current) return;
    totpInitRef.current = true;
    startTotpSetup();
  }, []);

  useEffect(() => {
    if (step === 'sms_verify') setResendCountdown(30);
  }, [step]);

  useEffect(() => {
    if (resendCountdown <= 0) return;
    const timer = setTimeout(() => setResendCountdown((c) => c - 1), 1000);

    return () => clearTimeout(timer);
  }, [resendCountdown]);

  const handleChooseMethod = (method: MfaMethod) => {
    setChosenMethod(method);
    setError('');
    setCode('');
    if (method === 'totp') {
      startTotpSetup();
    } else {
      startSmsSetup();
    }
  };

  if (step === 'success') {
    const successLabel =
      chosenMethod === 'sms'
        ? 'SMS verification set up'
        : 'Authenticator set up';

    return (
      <SetupContainer>
        <div className="p-6 text-center">
          <div className="mx-auto size-16 animate-success-pop text-sr-text-success">
            <Icon className="size-full" icon="success" weight="fill" />
          </div>
          <h4 className="pt-4 text-lg font-bold">{successLabel}</h4>
          <p className="pb-6 pt-2 text-sr-text-secondary">
            Your account is now protected with multi-factor authentication.
          </p>
          <button
            className="w-full rounded-md bg-sr-text-brand px-4 py-3 text-sm font-medium text-white hover:bg-sr-text-brand-hover active:bg-sr-text-brand-active"
            type="button"
            onClick={onComplete}
          >
            Continue to Screenrights
          </button>
        </div>
      </SetupContainer>
    );
  }

  if (step === 'choose') {
    return (
      <SetupContainer>
        <MethodChooser
          chosenMethod={chosenMethod}
          error={error}
          isLoading={isLoading}
          onBack={() => startTotpSetup()}
          onChoose={handleChooseMethod}
        />
      </SetupContainer>
    );
  }

  if (step === 'phone') {
    return (
      <SetupContainer>
        <div className="p-6">
          <div className="text-center">
            <h4 className="text-lg font-bold">Enter your phone number</h4>
            <p className="pb-4 pt-2 text-sm text-sr-text-secondary">
              We&apos;ll send a verification code to this number each time you
              sign in.
            </p>
          </div>

          {error && <ErrorBanner message={error} />}

          <label
            className="mb-1 block text-sm font-medium"
            htmlFor="mfa-phone-input"
          >
            Phone number
          </label>
          <PhoneInputField
            isLoading={isLoading}
            value={phoneNumber}
            onChange={setPhoneNumber}
            onSubmit={handlePhoneSubmit}
          />

          <button
            className="mt-6 w-full rounded-md bg-sr-text-brand px-4 py-3 text-sm font-medium text-white hover:bg-sr-text-brand-hover active:bg-sr-text-brand-active disabled:cursor-not-allowed disabled:opacity-50"
            disabled={
              isLoading || !phoneNumber || !isValidPhoneNumber(phoneNumber)
            }
            type="button"
            onClick={handlePhoneSubmit}
          >
            {isLoading ? 'Sending code...' : 'Send verification code'}
          </button>

          <button
            className="mt-5 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
            type="button"
            onClick={() => {
              setError('');
              setCode('');
              setStep('choose');
            }}
          >
            Back
          </button>
        </div>
      </SetupContainer>
    );
  }

  if (step === 'sms_verify') {
    const maskedPhone = phoneNumber
      ? phoneNumber.slice(0, -4).replace(/\d/g, '*') + phoneNumber.slice(-4)
      : '';

    return (
      <SetupContainer>
        <div className="p-6">
          <div className="text-center">
            <h4 className="text-lg font-bold">Verify your phone number</h4>
            <p className="pb-4 pt-2 text-sm text-sr-text-secondary">
              Enter the 6-digit code sent to {maskedPhone}. It may take a minute
              to arrive.
            </p>
          </div>

          {error && <ErrorBanner message={error} />}

          <CodeInput
            isLoading={isLoading}
            value={code}
            onChange={setCode}
            onSubmit={handleSmsVerify}
          />

          <button
            className="mt-6 w-full rounded-md bg-sr-text-brand px-4 py-3 text-sm font-medium text-white hover:bg-sr-text-brand-hover active:bg-sr-text-brand-active disabled:cursor-not-allowed disabled:opacity-50"
            disabled={isLoading || code.trim().length !== 6}
            type="button"
            onClick={handleSmsVerify}
          >
            {isLoading ? 'Verifying...' : 'Verify and activate'}
          </button>

          <button
            className="mt-4 w-full rounded-md border border-sr-text-brand bg-transparent px-4 py-3 text-sm font-medium text-sr-text-brand hover:border-sr-dark-red hover:text-sr-dark-red disabled:cursor-not-allowed disabled:border-sr-outline disabled:text-sr-light-gray"
            disabled={resendCountdown > 0}
            type="button"
            onClick={resendSmsCode}
          >
            {resendCountdown > 0
              ? `Resend code (${resendCountdown}s)`
              : 'Resend code'}
          </button>

          {resendMessage && (
            <p className="pt-1 text-center text-sm text-sr-text-success">
              {resendMessage}
            </p>
          )}

          <button
            className="mt-6 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
            type="button"
            onClick={() => {
              setCode('');
              setError('');
              setResendMessage('');
              setResendCountdown(0);
              setStep('phone');
            }}
          >
            Change phone number
          </button>
        </div>
      </SetupContainer>
    );
  }

  // TOTP QR + verify step (default)
  return (
    <SetupContainer>
      <div className="p-6">
        <div className="text-center">
          <h4 className="text-lg font-bold">
            Set up your multi-factor authentication
          </h4>
          <p className="pt-2 text-sm text-sr-text-secondary">
            Scan the QR code below with an authenticator app on your phone such
            as Google Authenticator, Microsoft Authenticator or Authy.
          </p>
        </div>

        {qrDataUrl ? (
          <div className="pb-4 pt-6 text-center">
            <div className="inline-block rounded-lg border border-sr-highlight bg-white p-4">
              <img
                alt="Scan this QR code with your authenticator app"
                height={200}
                src={qrDataUrl}
                width={200}
              />
            </div>
          </div>
        ) : (
          <div className="flex min-h-56 items-center justify-center p-8 text-center">
            <Spinner size="8" />
          </div>
        )}

        <div className="pb-4 text-center">
          <button
            className="text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
            type="button"
            onClick={() => setShowSecret(!showSecret)}
          >
            {showSecret ? 'Hide' : "Can't scan? Enter key manually"}
          </button>
          {showSecret && (
            <div className="mt-2 rounded-md bg-neutral-100 p-2">
              <p className="select-all break-all font-mono text-xs font-bold">
                {secretKey}
              </p>
            </div>
          )}
        </div>

        {error && <ErrorBanner message={error} />}

        <CodeInput
          isLoading={isLoading}
          value={code}
          onChange={setCode}
          onSubmit={handleTotpVerify}
        />

        <button
          className="mt-6 w-full rounded-md bg-sr-text-brand px-4 py-3 text-sm font-medium text-white hover:bg-sr-text-brand-hover active:bg-sr-text-brand-active disabled:cursor-not-allowed disabled:opacity-50"
          disabled={isLoading || code.trim().length !== 6}
          type="button"
          onClick={handleTotpVerify}
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </button>

        <button
          className="mt-6 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
          type="button"
          onClick={() => {
            resetState();
            setStep('choose');
          }}
        >
          Choose other method
        </button>

        {onCancel && (
          <button
            className="mt-5 w-full bg-transparent text-sm font-medium text-sr-text-brand hover:text-sr-text-brand-hover"
            type="button"
            onClick={onCancel}
          >
            Cancel
          </button>
        )}
      </div>
    </SetupContainer>
  );
};

export default MfaSetupScreen;
